/**
 * The words and numbers on the export page, kept out of the component so they
 * can be checked without rendering anything.
 *
 * The export runs in batches and the page drives it, so what somebody reads
 * while they wait is the only evidence they have that it is working. It has to
 * say which part of the library is being read, not "please wait".
 */

export type ExportPhase =
  | 'manifest'
  | 'acknowledgements'
  | 'legacy'
  | 'documents'
  | 'report'
  | 'done';

export type ExportStatus = 'running' | 'complete' | 'failed';

export interface ExportCounts {
  policies: number;
  /** Policies whose files are already in a part — what the bar reads. */
  policiesExported: number;
  versions: number;
  files: number;
  fileBytes: number;
  acknowledgements: number;
  legacyAcknowledgements: number;
  problems: number;
}

export interface ExportPart {
  name: string;
  sizeBytes: number;
  contains: string;
  /** Minted per request and short-lived; null when it could not be minted. */
  url: string | null;
}

export interface ExportProblem {
  where: string;
  what: string;
  why: string;
}

export interface ExportJob {
  exportId: string;
  status: ExportStatus;
  phase: ExportPhase;
  counts: ExportCounts;
  policyTotal: number;
  problems: ExportProblem[];
  workbookIncluded: boolean;
  requestedByEmail: string | null;
  requestedByName: string | null;
  error: string | null;
  parts: ExportPart[];
  retentionDays: number;
}

export interface ExportSummary {
  id: string;
  status: ExportStatus;
  phase: ExportPhase;
  requestedAtIso: string | null;
  completedAtIso: string | null;
  requestedByEmail: string | null;
  requestedByName: string | null;
  counts: ExportCounts | null;
  partCount: number;
  purged: boolean;
  error: string | null;
}

/** What is being read right now, in the customer's words rather than ours. */
export const PHASE_LABEL: Record<ExportPhase, string> = {
  manifest: 'Listing your policies and their versions',
  acknowledgements: 'Collecting your acknowledgement records',
  legacy: 'Collecting the attestations imported from your previous system',
  documents: 'Adding your document files',
  report: 'Finishing the archive',
  done: 'Finished',
};

/**
 * A size somebody can judge at a glance. Bytes below a kilobyte, then one
 * decimal place: "0.9 MB" is a fact and "943718 bytes" is a puzzle.
 */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${Math.round(bytes)} bytes`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  return `${(mb / 1024).toFixed(2)} GB`;
}

/** A moment, in the reader's own time zone, with the minute kept. */
export function formatMoment(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * The line under the progress bar.
 *
 * It names the phase and, while the documents are going in, how far through
 * the library it is — which is the only number that moves for most of a large
 * export. It never invents a percentage for a phase that has no total.
 */
export function progressLine(job: Pick<ExportJob, 'phase' | 'counts' | 'policyTotal' | 'status'>): string {
  if (job.status === 'complete') {
    return `${job.counts.policies} ${plural(job.counts.policies, 'policy', 'policies')}, ${job.counts.files} ${plural(job.counts.files, 'document', 'documents')}, ${job.counts.acknowledgements + job.counts.legacyAcknowledgements} acknowledgement ${plural(job.counts.acknowledgements + job.counts.legacyAcknowledgements, 'record', 'records')}.`;
  }
  if (job.status === 'failed') return 'The export stopped.';
  const label = PHASE_LABEL[job.phase] ?? 'Working';
  if (job.phase === 'manifest' && job.policyTotal > 0) {
    return `${label}: ${job.counts.policies} of ${job.policyTotal}.`;
  }
  if (job.phase === 'documents') {
    return `${label}: ${job.counts.policiesExported} of ${job.policyTotal} policies, ${job.counts.files} ${job.counts.files === 1 ? 'file' : 'files'}, ${formatBytes(job.counts.fileBytes)}.`;
  }
  if (job.phase === 'acknowledgements') return `${label}: ${job.counts.acknowledgements} so far.`;
  if (job.phase === 'legacy') return `${label}: ${job.counts.legacyAcknowledgements} so far.`;
  return `${label}…`;
}

/**
 * How far along, as a fraction, for the bar.
 *
 * The phases are weighted by how long they actually take: reading every file
 * is most of the work, so it is most of the bar. A bar that sat at 90% for
 * eight minutes would be a lie told slowly.
 */
const PHASE_WEIGHT: Record<ExportPhase, [start: number, end: number]> = {
  manifest: [0, 0.15],
  acknowledgements: [0.15, 0.2],
  legacy: [0.2, 0.25],
  documents: [0.25, 0.97],
  report: [0.97, 1],
  done: [1, 1],
};

export function progressFraction(
  job: Pick<ExportJob, 'phase' | 'counts' | 'policyTotal' | 'status'>,
): number {
  if (job.status === 'complete') return 1;
  const [start, end] = PHASE_WEIGHT[job.phase] ?? [0, 1];
  if (job.policyTotal <= 0) return start;
  /**
   * `policies` is complete by the end of the manifest phase, so the documents
   * phase reads its own counter. Using the same one for both would park the bar
   * at the start of the documents phase for the whole of the documents phase,
   * which is most of the wait.
   */
  const through =
    job.phase === 'manifest'
      ? Math.min(job.counts.policies / job.policyTotal, 1)
      : job.phase === 'documents'
        ? Math.min(job.counts.policiesExported / job.policyTotal, 1)
        : 0;
  return Math.min(start + (end - start) * through, 1);
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

/**
 * Whether the page should ask for another batch.
 *
 * The engine does as much as fits in one invocation and asks to be called
 * again, exactly as the library import does. A finished or failed export is
 * never asked again — a loop that kept calling a failed job would hammer a
 * function that is already failing.
 */
export function shouldContinue(job: Pick<ExportJob, 'status'> | null | undefined): boolean {
  return job?.status === 'running';
}

/** What the list of previous exports says in its status column. */
export function summaryStatus(row: Pick<ExportSummary, 'status' | 'purged' | 'partCount'>): string {
  if (row.status === 'failed') return 'Stopped';
  if (row.status === 'running') return 'Being built';
  if (row.purged) return 'Finished — files removed';
  return `Finished — ${row.partCount} ${row.partCount === 1 ? 'file' : 'files'}`;
}
